import { AgGridReact } from 'ag-grid-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { useAgGridCommon } from 'common/table/agGrid/AgGridCommon';
import { commonPaginationOptions, mergeGridOpts } from 'utils/agGrid/options';
import { useAppDispatch } from 'hooks/useAppDispatch';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import { fetchRiskPromise } from 'reducers/riskReducer';
import columnDefs from './columnDefs';
import EnvPromise from '../../../config/env';
import { processSSRM } from 'utils/agGrid/ssrm/processSSRM';

const gridOptions = mergeGridOpts(commonPaginationOptions, {
  maintainColumnOrder: true,
  defaultColDef: {
    floatingFilter: false,
    sortable: false,
  },
});

export const RISK_ENGINE_GRID_NAME = 'riskEngine';

const RiskEngineGrid = () => {
  const [riskDataFromTcs, setRiskDataFromTcs] = useState<string>('');
  const gridRef = useRef<AgGridReact>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    EnvPromise.then((e) => setRiskDataFromTcs(e?.readRiskDataFromTcs));
  }, []);

  const { gridVisible, initialGridSettings, onStateUpdated, gridButtons } =
    useAgGridCommon({
      gridName: RISK_ENGINE_GRID_NAME,
      gridRef,
    });

  const fetchData = useCallback(
    async (params: IServerSideGetRowsParams) => {
      const { request } = params;
      const body = parseAGRequest(request, { transformAttrs: true });
      const response = await fetchRiskPromise(body, dispatch);
      return {
        rows: response?.riskAccountSummaries,
        count: response.count,
      };
    },
    [dispatch],
  );

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => processSSRM(params, fetchData),
    [fetchData],
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      const dataSource: IServerSideDatasource = {
        getRows,
      };
      params.api?.setGridOption('serverSideDatasource', dataSource);
    },
    [getRows],
  );

  const memoizedColumnDefs = useMemo(
    () => columnDefs(riskDataFromTcs),
    [riskDataFromTcs],
  );

  return (
    <div className="ag-theme-quartz">
      {gridVisible ? (
        <>
          {gridButtons}
          <AgGridReact
            ref={gridRef}
            columnDefs={memoizedColumnDefs}
            gridOptions={gridOptions}
            onStateUpdated={onStateUpdated}
            initialState={initialGridSettings}
            rowModelType="serverSide"
            onGridReady={onGridReady}
          />
        </>
      ) : null}
    </div>
  );
};

export default RiskEngineGrid;
